import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { CheckCircle, Clock, AlertTriangle, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TasksManagement = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_id: '',
    priority: 'medium',
    status: 'pending',
    due_date: '',
    category: 'maintenance'
  });

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, propertiesRes] = await Promise.all([
        axios.get(`${API}/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTasks(tasksRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Task Created!", description: "Task has been added successfully" });
      setShowAddModal(false);
      setFormData({ title: '', description: '', property_id: '', priority: 'medium', status: 'pending', due_date: '', category: 'maintenance' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create task", variant: "destructive" });
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await axios.post(`${API}/tasks/${taskId}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Task Completed!", description: "Task marked as completed" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update task", variant: "destructive" });
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: "Task Deleted" });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete task", variant: "destructive" });
    }
  };

  const filteredTasks = tasks.filter(t => {
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    return matchesStatus && matchesPriority;
  });

  const stats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    high: tasks.filter(t => t.priority === 'high' && t.status !== 'completed').length
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 p-6 space-y-6">
      <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Tasks Management</h1>
            <p className="text-gray-600 mt-2">Track and manage property tasks</p>
          </div>
          <Button onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105">
            <Plus size={20} className="mr-2" />
            Add Task
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="backdrop-blur-xl bg-gradient-to-br from-blue-500/90 to-indigo-500/90 rounded-3xl shadow-2xl border border-white/50 p-6 text-white">
          <p className="text-blue-100 text-sm">Total Tasks</p>
          <p className="text-5xl font-bold mt-2">{stats.total}</p>
        </div>
        <div className="backdrop-blur-xl bg-gradient-to-br from-yellow-500/90 to-orange-500/90 rounded-3xl shadow-2xl border border-white/50 p-6 text-white">
          <p className="text-yellow-100 text-sm">Pending</p>
          <p className="text-5xl font-bold mt-2">{stats.pending}</p>
        </div>
        <div className="backdrop-blur-xl bg-gradient-to-br from-green-500/90 to-emerald-500/90 rounded-3xl shadow-2xl border border-white/50 p-6 text-white">
          <p className="text-green-100 text-sm">Completed</p>
          <p className="text-5xl font-bold mt-2">{stats.completed}</p>
        </div>
        <div className="backdrop-blur-xl bg-gradient-to-br from-red-500/90 to-pink-500/90 rounded-3xl shadow-2xl border border-white/50 p-6 text-white">
          <p className="text-red-100 text-sm">High Priority</p>
          <p className="text-5xl font-bold mt-2">{stats.high}</p>
        </div>
      </div>

      <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-4">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setStatusFilter('all')} className={`px-6 py-3 rounded-full font-medium transition-all ${statusFilter === 'all' ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg' : 'bg-white/80 text-gray-700'}`}>All</button>
          <button onClick={() => setStatusFilter('pending')} className={`px-6 py-3 rounded-full font-medium transition-all ${statusFilter === 'pending' ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white shadow-lg' : 'bg-white/80 text-gray-700'}`}>Pending</button>
          <button onClick={() => setStatusFilter('completed')} className={`px-6 py-3 rounded-full font-medium transition-all ${statusFilter === 'completed' ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg' : 'bg-white/80 text-gray-700'}`}>Completed</button>
          <div className="flex-1"></div>
          <button onClick={() => setPriorityFilter('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${priorityFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-white/80 text-gray-700'}`}>All Priority</button>
          <button onClick={() => setPriorityFilter('high')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${priorityFilter === 'high' ? 'bg-red-600 text-white' : 'bg-white/80 text-gray-700'}`}>High</button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {filteredTasks.length === 0 ? (
          <div className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-12 text-center">
            <CheckCircle className="w-20 h-20 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Tasks Found</h3>
            <p className="text-gray-600">All caught up!</p>
          </div>
        ) : (
          filteredTasks.map((task) => (
            <div key={task.id} className="backdrop-blur-xl bg-white/70 rounded-3xl shadow-xl border border-white/50 p-6 hover:shadow-2xl transition-all hover:scale-[1.01]">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg ${task.priority === 'high' ? 'bg-gradient-to-br from-red-500 to-pink-500' : task.priority === 'medium' ? 'bg-gradient-to-br from-yellow-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-indigo-500'}`}>
                      {task.status === 'completed' ? <CheckCircle size={28} /> : task.priority === 'high' ? <AlertTriangle size={28} /> : <Clock size={28} />}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{task.title}</h3>
                      <div className="flex gap-2 mt-1">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${task.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{task.status}</span>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${task.priority === 'high' ? 'bg-red-100 text-red-800' : task.priority === 'medium' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>{task.priority}</span>
                        <span className="text-xs px-3 py-1 rounded-full bg-purple-100 text-purple-800 font-semibold">{task.category}</span>
                      </div>
                    </div>
                  </div>
                  {task.description && <p className="text-gray-600 mb-3">{task.description}</p>}
                  {task.due_date && (
                    <p className="text-sm text-gray-500">Due: {task.due_date}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {task.status !== 'completed' && (
                    <Button size="sm" onClick={() => handleComplete(task.id)} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 rounded-2xl shadow-md">
                      <CheckCircle size={16} className="mr-1" />
                      Complete
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => handleDelete(task.id)} className="rounded-2xl border-red-300 text-red-600 hover:bg-red-50">
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">Create New Task</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="rounded-2xl" required />
              </div>
              <div>
                <Label>Property</Label>
                <select value={formData.property_id} onChange={(e) => setFormData({...formData, property_id: e.target.value})} className="w-full px-4 py-2.5 border rounded-2xl focus:ring-2 focus:ring-blue-500">
                  <option value="">Select Property</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Priority</Label>
                <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-4 py-2.5 border rounded-2xl focus:ring-2 focus:ring-blue-500">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <Label>Category</Label>
                <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-4 py-2.5 border rounded-2xl focus:ring-2 focus:ring-blue-500">
                  <option value="maintenance">Maintenance</option>
                  <option value="inspection">Inspection</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="repair">Repair</option>
                  <option value="cleaning">Cleaning</option>
                </select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="rounded-2xl" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2.5 border rounded-2xl focus:ring-2 focus:ring-blue-500" rows="3" />
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-2xl shadow-lg">
                Create Task
              </Button>
              <Button type="button" onClick={() => setShowAddModal(false)} variant="outline" className="flex-1 rounded-2xl">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksManagement;
